import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { type StringSchema, string } from "yup";

import { validateDateWithLocale } from "./date-validation";

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

/**
 * Shared person-identity field validators so every form capturing a person's legal details (KYC
 * key-individuals, the user profile, …) validates identically. Builders are intl-agnostic — callers
 * pass formatted messages and assign the schema to their own field name.
 */

/** Minimum / maximum age (years) for a person's date of birth. */
export const MIN_PERSON_AGE_YEARS = 16;
export const MAX_PERSON_AGE_YEARS = 140;

/** Year range for a date-of-birth year picker, derived from the person age bounds above. */
export const getDobYearPickerRange = () => {
  const currentYear = new Date().getFullYear();
  return { min: currentYear - MAX_PERSON_AGE_YEARS, max: currentYear - MIN_PERSON_AGE_YEARS };
};

/** A required legal-name field: a non-empty string (whitespace-only is treated as empty). */
export const legalNameField = (requiredMessage: string): StringSchema =>
  string().trim().required(requiredMessage).min(1, requiredMessage);

export type DateOfBirthMessages = {
  invalid: string;
  tooYoung: string;
  tooOld: string;
};

/**
 * A nullable date-of-birth field stored as a date string. Empty / "--" coalesces to null; when present
 * it must be a valid date, at least {@link MIN_PERSON_AGE_YEARS} and at most {@link MAX_PERSON_AGE_YEARS}
 * years old.
 */
export const dateOfBirthField = ({ invalid, tooYoung, tooOld }: DateOfBirthMessages) =>
  string()
    .nullable()
    .transform((value) => (!value || value === "--" ? null : value))
    .test("isValid", invalid, (value) => (!value ? true : validateDateWithLocale(value, (parsed) => parsed.isValid())))
    .test("isOldEnough", tooYoung, (value) =>
      !value
        ? true
        : validateDateWithLocale(value, (parsed) =>
            parsed.isSameOrBefore(dayjs().subtract(MIN_PERSON_AGE_YEARS, "years"))
          )
    )
    .test("isAlive", tooOld, (value) =>
      !value
        ? true
        : validateDateWithLocale(value, (parsed) =>
            // Compare at day granularity so a DOB on the 140th birthday isn't rejected by the
            // time-of-day carried in `dayjs()` (the parsed DOB is midnight, the threshold is "now").
            parsed.isSameOrAfter(dayjs().subtract(MAX_PERSON_AGE_YEARS, "years"), "day")
          )
    );

/** US 5-digit ZIP or ZIP+4. */
export const US_ZIP_REGEX = /(^\d{5}$)|(^\d{5}-\d{4}$)/;

export const isUsZip = (value: string): boolean => US_ZIP_REGEX.test(value);

/** Canonical optional-address shape for completeness checks; consumers map their own keys to these. */
export type AddressForCompleteness = {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
};

/**
 * Whether an optional address is "complete enough": an entirely-empty address is allowed, but once any
 * field is set, line1 + city + country + postcode become required (and state when the country is US).
 */
export const isAddressComplete = (address: AddressForCompleteness | null | undefined): boolean => {
  if (!address) {
    return true;
  }
  // Normalise to treat whitespace-only fields as empty, so a string of spaces can neither mark the
  // address as "started" nor satisfy a required field.
  const line1 = address.line1?.trim();
  const line2 = address.line2?.trim();
  const city = address.city?.trim();
  const state = address.state?.trim();
  const postcode = address.postcode?.trim();
  const country = address.country?.trim();
  const isEmpty = !line1 && !line2 && !city && !state && !postcode && !country;
  if (isEmpty) {
    return true;
  }
  if (!line1 || !city || !country || !postcode) {
    return false;
  }
  if (country === "US" && !state) {
    return false;
  }
  return true;
};
